import { FormEvent, useState } from 'react';
import type { ExpenseCostSplit, ModernizationCostSplit } from '../../api';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import {
  OWNER_SPLITS,
  ownerSplitError,
  ownerSplitFromSaved,
  ownerSplitRequest,
} from '../../utils/expenseSplit';
import ExpenseSplitFields from '../ExpenseSplitFields/ExpenseSplitFields';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';

const todayIso = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

export type AddModernizationPayload = {
  description: string;
  performed_by: string;
  performed_at: string;
  cost: number;
  cost_split: ModernizationCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  comment: string;
};

type Props = {
  title?: string;
  confirmLabel?: string;
  initial?: Partial<AddModernizationPayload> | null;
  onClose: () => void;
  onConfirm: (data: AddModernizationPayload) => void | Promise<void>;
};

const toDateInput = (value?: string | null) => {
  if (!value) return todayIso();
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return todayIso();
};

const AddModernizationModal = ({
  title = 'Добавить модернизацию',
  confirmLabel = 'Добавить',
  initial,
  onClose,
  onConfirm,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const [initialSplit] = useState(() =>
    ownerSplitFromSaved(
      initial?.cost_split,
      initial?.payer_ids,
      initial?.payer_shares
    )
  );
  const [form, setForm] = useState({
    description: initial?.description || '',
    performed_by: initial?.performed_by || '',
    performed_at: toDateInput(initial?.performed_at),
    cost: initial?.cost != null ? String(initial.cost) : '',
    cost_split: initialSplit.costSplit,
    comment: initial?.comment || '',
  });
  const [manualShares, setManualShares] = useState<Record<number, string>>(
    initialSplit.manualShares
  );
  const amountValue = Number(String(form.cost).replace(',', '.'));
  const previewAmount =
    form.cost.trim() !== '' && Number.isFinite(amountValue) && amountValue >= 0
      ? amountValue
      : null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const cost = Number(String(form.cost).replace(',', '.'));
    if (!form.description.trim()) {
      showError('Укажите описание улучшения');
      return;
    }
    if (!form.performed_at) {
      showError('Укажите дату');
      return;
    }
    if (!form.performed_by.trim()) {
      showError('Укажите, кем сделано');
      return;
    }
    if (!Number.isFinite(cost) || cost < 0) {
      showError('Укажите стоимость в рублях');
      return;
    }
    const split: ExpenseCostSplit = cost > 0 ? form.cost_split : 'shares';
    const splitError = ownerSplitError(split, manualShares);
    if (splitError) {
      showError(splitError);
      return;
    }
    const { cost_split, payer_ids, payer_shares } = ownerSplitRequest(
      split,
      manualShares
    );
    void runSubmit(() =>
      onConfirm({
        description: form.description.trim(),
        performed_by: form.performed_by.trim(),
        performed_at: form.performed_at,
        cost,
        cost_split: cost_split as ModernizationCostSplit,
        payer_ids,
        payer_shares,
        comment: form.comment.trim(),
      })
    );
  };

  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>{title}</h3>
          <div className="modal-body">
            <label>Описание улучшения</label>
            <input
              type="text"
              placeholder="Что сделано"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              required
            />
            <label>Когда</label>
            <input
              type="date"
              value={form.performed_at}
              onChange={(e) =>
                setForm({ ...form, performed_at: e.target.value })
              }
              max={todayIso()}
              required
            />
            <label>Кем сделано</label>
            <input
              type="text"
              placeholder="ФИО / организация"
              value={form.performed_by}
              onChange={(e) =>
                setForm({ ...form, performed_by: e.target.value })
              }
              required
            />
            <label>Стоимость, ₽</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              value={form.cost}
              onChange={(e) => setForm({ ...form, cost: e.target.value })}
              required
            />
            <label>Комментарий</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этой модернизации..."
              rows={5}
            />
            <ExpenseSplitFields
              date={form.performed_at}
              amount={previewAmount}
              expenseType="modernization"
              costSplit={form.cost_split}
              manualShares={manualShares}
              flightIds={[]}
              onCostSplitChange={(cost_split) => setForm({ ...form, cost_split })}
              onManualSharesChange={setManualShares}
              onFlightIdsChange={() => undefined}
              radioName="mod-cost-split"
              allowedSplits={OWNER_SPLITS}
            />
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : confirmLabel}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AddModernizationModal;
